import { HelpCircle } from "lucide-react";
import * as React from "react";

import Tooltip from "../tooltip/Tooltip";

import { cn } from "@/common/utils/utils";

type HelpIconProps = {
  /** Help text shown in the tooltip. Plain string or rich React node. */
  content: React.ReactNode;
  /** Optional URL — renders a "Learn more" link in the tooltip. */
  learnMoreHref?: string;
  /** Accessible label. Defaults to "Help". Used for screen readers and aria-label. */
  ariaLabel?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  /** Icon size in px. Default 14. */
  size?: number;
  className?: string;
};

/**
 * Inline `?` icon that opens a help tooltip on hover/focus. Use next to section
 * headers, jargon labels, or any control where users need quick guidance.
 *
 * @example
 *   <label>
 *     Severity <HelpIcon content="How badly does this break the product?" />
 *   </label>
 */
const HelpIcon: React.FC<HelpIconProps> = ({
  content,
  learnMoreHref,
  ariaLabel = "Help",
  side = "top",
  align = "center",
  size = 14,
  className,
}) => {
  const tooltipContent = learnMoreHref ? (
    <div className="flex flex-col gap-1.5 max-w-xs">
      <div>{content}</div>
      <a
        href={learnMoreHref}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[11px] underline underline-offset-2 opacity-90 hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        Learn more →
      </a>
    </div>
  ) : (
    <div className="max-w-xs">{content}</div>
  );

  return (
    <Tooltip content={tooltipContent} side={side} align={align}>
      <button
        type="button"
        aria-label={ariaLabel}
        className={cn(
          "inline-flex items-center justify-center text-text-tertiary hover:text-text-primary",
          "cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-9 focus-visible:ring-offset-1 rounded-full",
          "transition-colors duration-150",
          className,
        )}
        onClick={(e) => e.preventDefault()}
      >
        <HelpCircle width={size} height={size} aria-hidden="true" />
      </button>
    </Tooltip>
  );
};

export default HelpIcon;
